import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { RecipeSuggestion, SavedRecipe } from '../types';
import { useAppState } from './StateProvider';
import { getMakeAgainSuggestions } from '../lib/recipeSuggestions';
import {
  archiveRecipeRemote,
  dismissRecipeSuggestionRemote,
  loadRecipesRemote,
  markRecipeSuggestedRemote,
  saveMealAsRecipeRemote,
  startSavedRecipeRemote,
} from '../lib/recipes';

type RecipeCtx = {
  recipes: SavedRecipe[];
  suggestions: RecipeSuggestion[];
  loadingRecipes: boolean;
  recipeBusy: boolean;
  refreshRecipes: () => Promise<void>;
  saveMeal: (mealId: string, mealName: string) => Promise<boolean>;
  cookRecipe: (recipeId: string) => Promise<string | null>;
  dismissSuggestion: (recipeId: string) => Promise<void>;
  markSuggestionShown: (recipeId: string) => void;
  archiveRecipe: (recipeId: string, recipeName: string) => Promise<void>;
  isMealSaved: (mealId: string) => boolean;
};

const RecipeContext = createContext<RecipeCtx | null>(null);

export function RecipeProvider({ children }: { children: ReactNode }) {
  const { state, householdId, reload } = useAppState();
  const [recipes, setRecipes] = useState<SavedRecipe[]>([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [recipeBusy, setRecipeBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const markedThisSession = useRef(new Set<string>());
  const mealHistorySignature = useMemo(() => state.meals.map((meal) => `${meal.id}:${meal.status ?? ''}:${meal.cookedAt ?? ''}`).join('|'), [state.meals]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 5200);
    return () => clearTimeout(t);
  }, [notice]);

  async function refreshRecipes() {
    if (!householdId) { setRecipes([]); return; }
    setLoadingRecipes(true);
    try { setRecipes(await loadRecipesRemote(householdId)); }
    catch (error) { console.error(error); setNotice('Saved recipes could not be refreshed.'); }
    finally { setLoadingRecipes(false); }
  }

  useEffect(() => { void refreshRecipes(); }, [householdId, mealHistorySignature]);

  const suggestions = useMemo(() => getMakeAgainSuggestions(state, recipes, 3), [state.inventory, state.meals, recipes]);

  function markSuggestionShown(recipeId: string) {
    if (!householdId || markedThisSession.current.has(recipeId)) return;
    markedThisSession.current.add(recipeId);
    void markRecipeSuggestedRemote(householdId, recipeId).catch((error) => console.warn('Could not record recipe suggestion', error));
  }

  async function saveMeal(mealId: string, mealName: string) {
    if (recipeBusy) return false;
    setRecipeBusy(true);
    try {
      await saveMealAsRecipeRemote(mealId);
      await refreshRecipes();
      setNotice(`${mealName} saved to your household recipes.`);
      return true;
    } catch (error) {
      console.error(error);
      setNotice('That recipe could not be saved.');
      return false;
    } finally { setRecipeBusy(false); }
  }

  async function cookRecipe(recipeId: string) {
    if (recipeBusy) return null;
    setRecipeBusy(true);
    try {
      const mealId = await startSavedRecipeRemote(recipeId);
      await reload();
      setNotice('Saved recipe loaded. No new recipe generation needed.');
      return mealId;
    } catch (error) {
      console.error(error);
      setNotice('That saved recipe could not be started.');
      return null;
    } finally { setRecipeBusy(false); }
  }

  async function dismissSuggestion(recipeId: string) {
    if (!householdId) return;
    try {
      await dismissRecipeSuggestionRemote(householdId, recipeId, 7);
      const until = new Date(Date.now() + 7 * 86400000).toISOString();
      setRecipes((current) => current.map((recipe) => recipe.id === recipeId ? { ...recipe, dismissedUntil: until, lastSuggestedAt: new Date().toISOString() } : recipe));
      setNotice('Got it — Weekstead will give that recipe a break.');
    } catch (error) { console.error(error); setNotice('That suggestion could not be dismissed.'); }
  }

  async function archiveRecipe(recipeId: string, recipeName: string) {
    if (!householdId) return;
    try {
      await archiveRecipeRemote(householdId, recipeId);
      setRecipes((current) => current.filter((recipe) => recipe.id !== recipeId));
      setNotice(`${recipeName} removed from saved recipes.`);
    } catch (error) { console.error(error); setNotice('That saved recipe could not be removed.'); }
  }

  const isMealSaved = (mealId: string) => recipes.some((recipe) => recipe.sourceMealId === mealId);
  const value = useMemo(() => ({ recipes, suggestions, loadingRecipes, recipeBusy, refreshRecipes, saveMeal, cookRecipe, dismissSuggestion, markSuggestionShown, archiveRecipe, isMealSaved }), [recipes, suggestions, loadingRecipes, recipeBusy, householdId]);

  return <RecipeContext.Provider value={value}>{children}{notice ? <div className="toast recipe-toast">{notice}</div> : null}</RecipeContext.Provider>;
}

export function useRecipeMemory() {
  const value = useContext(RecipeContext);
  if (!value) throw new Error('useRecipeMemory must be used inside RecipeProvider');
  return value;
}